import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/add';
import { SkeletonItem } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { RIGHT_SIDEBAR_WIDTH } from '@atlassian/jira-software-releases-version-detail-shared/src/common/constants.tsx';
import { lazyAfterPaint, useLazyPhase } from '@atlassian/react-loosely-lazy';
import type { UserProjectPermissions } from '@atlassian/jira-software-releases-version-detail-shared/src/common/types.tsx';
import type { async_softwareReleasesApprovers_Approvers$key } from './__generated__/async_softwareReleasesApprovers_Approvers.graphql';
import { APPROVERS_SHOW_MORE_THRESHOLD } from './constants.tsx';
import messages from './messages.tsx';

const PADDING_SIZE = 24;
const BORDER_SIZE = 1;
const WIDTH_OFFSET = (PADDING_SIZE + BORDER_SIZE) * 2;

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyApprovers = lazyAfterPaint(
	() =>
		import(/* webpackChunkName: "async-releases-approvers" */ './index').then(
			(module) => module.Approvers,
		),
	{ ssr: false },
);

type Props = {
	jiraVersion: async_softwareReleasesApprovers_Approvers$key;
	userProjectPermissions: UserProjectPermissions;
};

export const Approvers = ({ jiraVersion, userProjectPermissions }: Props): React.JSX.Element => {
	const { startNextPhase } = useLazyPhase();
	useEffect(() => {
		startNextPhase();
	}, [startNextPhase]);

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment async_softwareReleasesApprovers_Approvers on JiraVersion {
				...ui_softwareReleasesApprovers_Approvers
			}
		`,
		jiraVersion,
	);

	const { formatMessage } = useIntl();

	const numberOfApproversToShow = APPROVERS_SHOW_MORE_THRESHOLD;

	return (
		<Placeholder
			name="releases-approvers"
			fallback={
				<ApproversCardWrapper>
					<Header>
						<ApproversTitle>{formatMessage(messages.approversTitle)}</ApproversTitle>
						<Button iconBefore={<AddIcon label="" />} appearance="subtle" />
					</Header>
					{[...Array(numberOfApproversToShow).keys()].map((i) => (
						<SkeletonItem key={i} isShimmering hasAvatar />
					))}
				</ApproversCardWrapper>
			}
		>
			<LazyApprovers jiraVersion={version} userProjectPermissions={userProjectPermissions} />
		</Placeholder>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ApproversTitle = styled.div({
	display: 'flex',
	alignItems: 'center',
	font: token('font.body'),
	fontWeight: token('font.weight.semibold'),

	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Header = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ApproversCardWrapper = styled.div({
	gap: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${RIGHT_SIDEBAR_WIDTH - WIDTH_OFFSET}px`,
	maxHeight: 392,
	overflow: 'auto',
	paddingTop: token('space.300'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
});
